import meleElement from 'meleElement'
import L from 'leaflet'
import 'leaflet-edgebuffer'
import msysListener from 'msysListener'
import { geohash2point, point2geohash } from './utils.js'

// Import managers and renderers
import LayerManager from './managers/LayerManager.js'
import TileManager from './managers/TileManager.js'
import ItemManager from './managers/ItemManager.js'
import ShapeRenderer from './renderers/ShapeRenderer.js'
import UnitRenderer from './renderers/UnitRenderer.js'

/**
 * Property definitions for the mele-skmap custom element
 * @private
 */
const PROPERTIES = _.extend({}, {
  autoinit: { type: 'boolean', description: 'Auto initialization of the map', default: true },
  center: { type: 'string', description: 'Map center as geohash', default: 'u2s1yh4dzj87' },
  zoom: { type: 'number', description: 'Current zoom level', default: 14, min: 5, max: 19 },
  minzoom: { type: 'number', description: 'Minimum zoom', default: 8, min: 8, max: 18 },
  maxzoom: { type: 'number', description: 'Maximum zoom', default: 19, min: 6, max: 19 },
  basemap: { type: 'string', description: 'Basemap repository name (e.g. map_osm)' },
  format: { type: 'string', description: 'Image format/extension of tiles (e.g. png, jpg)', default: 'png' },
  disabled: { type: 'boolean', description: 'Disable all interactions' },
  iconsize: { type: 'number', description: 'Default icon size in pixels', default: 60 },
  tilebuffer: { type: 'number', description: 'Tile buffer size (in tiles) for edge buffering', default: 5 },
})

/**
 * meleSkMap - Slovakia Map Web Component
 *
 * Custom HTML element (mele-skmap) that provides an interactive Leaflet-based map
 * with support for multiple layers, real-time updates, and custom rendering.
 *
 * @extends meleElement
 */
export default class meleSkMap extends meleElement {
  /**
   * Initialize the component's internal state
   */
  init() {
    this._layers = {} // For backwards compatibility
    this._tileOverlays = {} // For backwards compatibility
  }

  /**
   * Handle changes to the autoinit property
   */
  autoinitChanged(value) {
    if (value) {
      this.initLeaflet()
    }
  }

  /**
   * Cleanup lifecycle hook
   */
  disconnectedCallback() {
    // Fragment-park: module was backgrounded, not actually removed. Skip
    // teardown so Leaflet survives focus switches.
    if(this.isParkedDisconnect && this.isParkedDisconnect()) return

    // Cleanup ResizeObserver
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }

    // Clear resize interval
    if (this._resizeInterval) {
      clearInterval(this._resizeInterval)
      this._resizeInterval = null
    }

    // Cleanup managers
    if (this._tileManager) {
      this._tileManager.destroy()
    }

    // Destroy Leaflet map
    if (this._leaflet) {
      this._leaflet.remove()
      this._leaflet = null
    }

    // meleElement/HTMLElement define no disconnectedCallback
    super.disconnectedCallback?.()
  }

  /**
   * Setter pre callback, ktorý sa volá po každej zmene zoom úrovne mapy.
   *
   * Slúži ako notifikácia pre nadradený komponent (napr. `mmodSkMap`,
   * `mmodSkPatrolMap`), že používateľ priblížil alebo oddialil mapu.
   * Volaný je interne z `zoomend` Leaflet event handler-a nastaveného
   * v `initLeaflet()` cez optional chaining (`this.eventMapZoomed?.(zoom)`),
   * takže null/undefined callback nehodí výnimku.
   *
   * Typický use-case: rodičovský komponent perzistuje aktuálnu zoom
   * úroveň do user preferences, alebo prepočítava vlastné overlay vrstvy
   * závislé od zoomu (napr. cluster-ovanie marker-ov, level-of-detail
   * pri vlastných tile vrstvách).
   *
   * Setter callback vždy uloží do `_eventMapZoomed`, aj keď je null
   * alebo undefined. V takom prípade zaloguje warning cez `msysListener`
   * metódu `this.warn(...)`, pretože odregistrovanie callback-u je
   * legitímne (napr. počas teardown rodiča), ale zvyčajne indikuje chybu
   * v poradí inicializácie a stojí za upozornenie.
   *
   * Single-listener pattern: opakované priradenie prepíše predchádzajúci
   * callback bez ďalšieho varovania. Ak je potrebných viac listener-ov,
   * agregáciu si musí zariadiť volajúci (napr. wrapper funkciou, ktorá
   * dispatch-uje na pole sub-listener-ov).
   *
   * Idempotentné, bez ďalších side-effectov mimo logu a zápisu do
   * internej premennej.
   *
   * @param {function(number): void} callback - funkcia volaná po `zoomend`
   *   evente Leaflet mapy. Prijíma jeden argument: aktuálnu zoom úroveň
   *   ako celé číslo v rozsahu danom `minzoom`/`maxzoom` properties
   *   (default 8 az 19). Návratová hodnota je ignorovaná. Pri null/undefined
   *   sa callback odregistruje a zaloguje sa warning.
   *
   * @example
   * const map = document.querySelector('mele-skmap')
   * map.eventMapZoomed = (zoom) => {
   *   console.log('Map zoomed to level:', zoom)
   *   userPrefs.set('mapZoom', zoom)
   * }
   */
  set eventMapZoomed(callback) {
    if (!callback) {
      this.warn('eventMapZoomed callback must be provided')
    }
    this._eventMapZoomed = callback
  }

  /**
   * Getter pre aktuálne registrovaný zoom callback.
   *
   * Vracia funkciu uloženú v `_eventMapZoomed` cez setter, alebo
   * `undefined` ak callback ešte nikdy nebol nastavený. Používa sa
   * interne v `zoomend` handleri cez optional chaining, takže absencia
   * callback-u nie je chyba — len neaktívny event hook.
   *
   * @returns {(function(number): void) | undefined} aktuálny callback
   *   alebo `undefined` ak žiadny nie je registrovaný
   */
  get eventMapZoomed() {
    return this._eventMapZoomed;
  }

  /**
   * Setter pre callback, ktorý sa volá pri začiatku draggu mapy
   * (`dragstart` event).
   *
   * Registruje funkciu volanú v okamihu, keď používateľ začne ťahať
   * mapu prstom alebo myšou. Callback nedostáva žiadne argumenty —
   * slúži len ako signál "používateľ začal manuálne posúvať mapu".
   * Pre opakovaný update pozície počas draggu (priebežné `move`
   * eventy) tento hook nie je vhodný; tu ide len o jednorazové
   * spustenie na začiatku gesta.
   *
   * Typické use-cases:
   * - zrušenie auto-follow režimu (mapa prestane automaticky sledovať
   *   pozíciu vozidla/hliadky, lebo používateľ prevzal kontrolu),
   * - zatvorenie kontextového menu alebo otvoreného popup-u,
   * - zaznamenanie user-interaction-u pre telemetriu/analytiku.
   *
   * Setter sa správa rovnako ako `eventMapZoomed`: null/undefined je
   * akceptované (odregistrovanie callback-u), ale spôsobí warning log.
   * Volanie z handler-a používa optional chaining (`this.eventMapDragged?.()`),
   * takže prázdny stav nehodí výnimku.
   *
   * Idempotentné, single-listener pattern. Opakované priradenie prepíše
   * predchádzajúci callback bez upozornenia.
   *
   * @param {function(): void} callback - funkcia volaná pri `dragstart`
   *   evente Leaflet mapy. Bez argumentov, návratová hodnota ignorovaná.
   *   Pri null/undefined sa callback odregistruje a zaloguje sa warning.
   *
   * @example
   * const map = document.querySelector('mele-skmap')
   * map.eventMapDragged = () => {
   *   this.autoFollowEnabled = false  // user prevzal kontrolu
   *   this.closeOpenPopups()
   * }
   */
  set eventMapDragged(callback) {
    if (!callback) {
      this.warn('eventMapDragged callback must be provided')
    }
    this._eventMapDragged = callback
  }

  /**
   * Getter pre aktuálne registrovaný drag callback.
   *
   * Vracia funkciu uloženú v `_eventMapDragged` cez setter, alebo
   * `undefined` ak callback ešte nikdy nebol nastavený. Interne sa
   * volá z `dragstart` handler-a cez optional chaining.
   *
   * @returns {(function(): void) | undefined} aktuálny callback alebo
   *   `undefined` ak žiadny nie je registrovaný
   */
  get eventMapDragged() {
    return this._eventMapDragged
  }

  /**
   * Initialize the Leaflet map and all managers
   */
  initLeaflet() {
    if (this._leaflet) return

    // Create Leaflet map
    const center = geohash2point(this.center)
    this._leaflet = L.map(this, {
      center,
      zoom: this.zoom,
      minZoom: this.minzoom,
      maxZoom: this.maxzoom,
      zoomControl: false,
      attributionControl: false,
    })

    // Create popup pane above all other layers
    const popupHighPane = this._leaflet.createPane('popup-high')
    popupHighPane.style.zIndex = 800

    // Initialize managers with warn function bound to this
    const warnFn = this.warn.bind(this)

    // Per-element msysListener so the batched tile layer can call msysProxy directly
    this._listener = new msysListener(0)
    this._listener.publish('#ME', 'mmStart')

    this._layerManager = new LayerManager(this._leaflet, warnFn)
    this._tileManager = new TileManager(this._leaflet, {
      maxzoom: this.maxzoom,
      tilebuffer: this.tilebuffer,
      listener: this._listener,
      format: this.format,
      warnFn,
    })
    this._itemManager = new ItemManager(this._layerManager, warnFn)
    this._shapeRenderer = new ShapeRenderer(
      this._itemManager,
      this._layerManager,
      this._leaflet,
      { iconsize: this.iconsize, warnFn }
    )
    this._unitRenderer = new UnitRenderer(
      this._itemManager,
      this._layerManager,
      { warnFn }
    )

    // Render basemap if set
    if (this.basemap) {
      this._tileManager.setBasemap(this.basemap, this.format)
    }

    // Setup event handlers for zoom/center sync
    this._leaflet.on('moveend', () => {
      const newCenter = this._leaflet.getCenter()
      this.setProperty('center', point2geohash([newCenter.lat, newCenter.lng]), true)
    })

    this._leaflet.on('zoomend', () => {
      const zoom = this._leaflet.getZoom()
      this.setProperty('zoom', zoom, true)
      this.eventMapZoomed?.(zoom)
    })

    this._leaflet.on('dragstart', () => {
      this.eventMapDragged?.()
    })

    this._leaflet.on('click', () => {
      this._leaflet.closePopup()
    })

    // Setup ResizeObserver for responsive sizing
    this._resizeObserver = new ResizeObserver(() => {
      if (this._leaflet) {
        this._leaflet.invalidateSize()
      }
    })
    this._resizeObserver.observe(this)

    // Fallback resize check
    this._resizeInterval = setInterval(() => {
      if (this._leaflet) {
        this._leaflet.invalidateSize()
      }
    }, 1000)

    this.setProperty('disabled', this.disabled, undefined, true)
  }

  /**
   * Reload/refresh the basemap and all tile overlays
   */
  reload() {
    if (this._tileManager) {
      this._tileManager.reload()
    }
  }

  ///////////////////////////////
  ///// ATTRIBUTE HANDLERS //////
  ///////////////////////////////

  basemapChanged(value) {
    if (this._tileManager) {
      this._tileManager.setBasemap(value, this.format)
    }
  }

  formatChanged(value) {
    if (this._tileManager && this.basemap) {
      this._tileManager.setBasemap(this.basemap, value)
    }
  }

  zoomChanged(value, old, context_user_interaction) {
    if (context_user_interaction || !this._leaflet) return
    this._leaflet.setZoom(value)
  }

  centerChanged(value, old, context_user_interaction) {
    if (context_user_interaction || !this._leaflet || !value) return
    const coords = geohash2point(value)
    this._leaflet.panTo(coords)
  }

  minzoomChanged(value) {
    if (this._leaflet) {
      this._leaflet.setMinZoom(value)
    }
  }

  maxzoomChanged(value) {
    if (this._leaflet) {
      this._leaflet.setMaxZoom(value)
    }
    if (this._tileManager) {
      this._tileManager.setMaxZoom(value)
    }
  }

  disabledChanged(value) {
    if (!this._leaflet) return

    if (value) {
      this._leaflet.dragging.disable()
      this._leaflet.touchZoom.disable()
      this._leaflet.doubleClickZoom.disable()
      this._leaflet.scrollWheelZoom.disable()
      this._leaflet.boxZoom.disable()
      this._leaflet.keyboard.disable()
    } else {
      this._leaflet.dragging.enable()
      this._leaflet.touchZoom.enable()
      this._leaflet.doubleClickZoom.enable()
      this._leaflet.scrollWheelZoom.enable()
      this._leaflet.boxZoom.enable()
      this._leaflet.keyboard.enable()
    }
  }

  ///////////////////////////////
  ///// TILE OVERLAY METHODS ////
  ///////////////////////////////

  setTileOverlay(id, cfg = {}) {
    if (this._tileManager) {
      this._tileManager.setTileOverlay(id, cfg)
    }
  }

  removeTileOverlay(id) {
    if (this._tileManager) {
      this._tileManager.removeTileOverlay(id)
    }
  }

  getTileOverlays() {
    return this._tileManager ? this._tileManager.getTileOverlays() : []
  }

  //////////////////////////
  ///// LAYER METHODS //////
  //////////////////////////

  setLayer(id, config = {}) {
    if (this._layerManager) {
      this._layerManager.setLayer(id, config)
    }
  }

  removeLayer(id) {
    if (this._layerManager) {
      this._layerManager.removeLayer(id)
    }
  }

  setLayerVisibility(id, visible) {
    if (this._layerManager) {
      this._layerManager.setLayerVisibility(id, visible)
    }
  }

  getLayer(id) {
    return this._layerManager ? this._layerManager.getLayer(id) : undefined
  }

  getLayers() {
    return this._layerManager ? this._layerManager.getLayers() : []
  }

  hasLayer(id) {
    return this._layerManager ? this._layerManager.hasLayer(id) : false
  }

  /////////////////////////
  ///// ITEM METHODS //////
  /////////////////////////

  addItem(item) {
    return this._itemManager ? this._itemManager.addItem(item) : undefined
  }

  removeItem(id) {
    if (this._itemManager) {
      this._itemManager.removeItem(id)
    }
  }

  updateItem(id, updates) {
    if (this._itemManager) {
      this._itemManager.updateItem(id, updates)
    }
  }

  getItem(id) {
    return this._itemManager ? this._itemManager.getItem(id) : null
  }

  clearLayer(layerId) {
    if (this._itemManager) {
      this._itemManager.clearLayer(layerId)
    }
  }

  clearAllItems() {
    if (this._itemManager) {
      this._itemManager.clearAllItems()
    }
  }

  //////////////////////////
  ///// SHAPE METHODS //////
  //////////////////////////

  addShapeGroup(id, style, geometries, popupContent) {
    return this._shapeRenderer ? this._shapeRenderer.addShapeGroup(id, style, geometries, popupContent) : undefined
  }

  renderShape(shape, pan = false) {
    if (this._shapeRenderer) {
      this._shapeRenderer.renderShape(shape, pan)
    }
  }

  /////////////////////////
  ///// UNIT METHODS //////
  /////////////////////////

  toggleUnitsLayer(visible) {
    if (this._unitRenderer) {
      this._unitRenderer.toggleUnitsLayer(visible)
    }
  }

  updateMyself(myself) {
    if (this._unitRenderer) {
      this._unitRenderer.updateMyself(myself)
    }
  }

  updateUnit(unit) {
    if (this._unitRenderer) {
      this._unitRenderer.updateUnit(unit)
    }
  }

  renderUnit(unit, timeout) {
    if (this._unitRenderer) {
      this._unitRenderer.renderUnit(unit, timeout)
    }
  }

  /**
   * Set callback for unit selection events
   */
  set eventUnitSelected(callback) {
    this._eventUnitSelected = callback
    if (this._unitRenderer) {
      this._unitRenderer.setUnitSelectedCallback(callback)
    }
  }

  get eventUnitSelected() {
    return this._eventUnitSelected
  }

  /////////////////////////////
  ///// NAVIGATION METHODS ////
  /////////////////////////////

  panTo(itemId) {
    const item = this.getItem(itemId)
    if (!item) {
      this.warn('Item not found for panTo:', itemId)
      return
    }

    if (item.geometry.type !== 'Point') {
      this.warn('panTo only supports Point geometries:', itemId)
      return
    }

    // Don't auto-pan while the user is pinch-zooming or a zoom animation is running;
    // panTo would setView with a snapped stale zoom and revert the gesture
    if (this._leaflet.touchZoom?._zooming || this._leaflet._animatingZoom) return

    const [lat, lng] = item.geometry.coordinates
    this._leaflet.panTo([lat, lng])
  }

  fitBounds(coordsArray) {
    if (!Array.isArray(coordsArray) || coordsArray.length === 0) {
      this.warn('fitBounds requires a non-empty array of coordinates')
      return
    }

    const latLngs = coordsArray.map(coord => L.latLng(coord[0], coord[1]))
    const bounds = L.latLngBounds(latLngs)
    this._leaflet.fitBounds(bounds, { padding: [50, 50] })
  }
}

meleSkMap._properties = PROPERTIES
meleSkMap.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-skmap', meleSkMap)
